import React, { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axiosInstance';

const DeliveryBoyList = () => {
    const [deliveryBoys, setDeliveryBoys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [msg, setMsg] = useState('');

    const fetchDeliveryBoys = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get('/delivery/delivery-boys');
            const data = res.data.deliveryBoys || res.data.data || res.data || [];
            setDeliveryBoys(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching delivery boys:', err);
            setMsg('Failed to fetch delivery partners.');
            setDeliveryBoys([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeliveryBoys();
    }, []);

    const filtered = deliveryBoys.filter(d =>
        (d.name || '').toLowerCase().includes(search.toLowerCase()) ||
        (d.contact || '').toLowerCase().includes(search.toLowerCase()) ||
        (d.email || '').toLowerCase().includes(search.toLowerCase())
    );

    const getInitials = name =>
        (name || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();

    const getAddressStr = addr => {
        if (!addr) return '—';
        if (typeof addr === 'string') return addr;
        return [addr.street, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '—';
    };

    return (
        <div className="delivery-page">
            {/* Page Header */}
            <div className="ph">
                <div>
                    <h1>Delivery Partners</h1>
                    <p>{deliveryBoys.length} registered delivery boys</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
                {[
                    { label: 'Total Partners', val: deliveryBoys.length },
                    { label: 'Active Today', val: deliveryBoys.filter(d => d.isActive !== false).length },
                ].map(s => (
                    <div key={s.label} className="card" style={{ padding: '12px 18px', flex: '1', minWidth: 130 }}>
                        <div style={{ fontSize: 10, color: 'var(--tx2)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em' }}>
                            {s.label}
                        </div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--tx)', marginTop: 5 }}>
                            {s.val}
                        </div>
                    </div>
                ))}
            </div>

            {/* Search Toolbar */}
            <div className="toolbar">
                <div className="search-box">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                    </svg>
                    <input
                        placeholder="Search by name, contact, or email…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && <button className="clear-btn" onClick={() => setSearch('')}>✕</button>}
                </div>
                <span style={{ fontSize: 12, color: 'var(--tx2)', flexShrink: 0 }}>
                    {filtered.length} of {deliveryBoys.length} partners
                </span>
            </div>

            {msg && (
                <div className="alert alert-error" style={{ marginBottom: 12 }}>
                    {msg}
                    <button className="alert-close" onClick={() => setMsg('')}>✕</button>
                </div>
            )}

            {/* Table */}
            <div className="tbl-wrap">
                <div className="tbl-scroll">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner" />
                            <p>Fetching delivery partners…</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-truck" style={{ fontSize: 28, color: 'var(--bdr)' }} />
                            <p>{search ? 'No delivery partners match your search.' : 'No delivery partners found.'}</p>
                        </div>
                    ) : (
                        <table className="tbl" style={{ minWidth: 780 }}>
                            <thead>
                                <tr>
                                    <th style={{ width: 36 }}>#</th>
                                    <th>Delivery Partner</th>
                                    <th>Contact</th>
                                    <th>Email</th>
                                    <th>Address</th>
                                    <th>Active Orders</th>
                                    <th>Completed</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((boy, i) => (
                                    <tr key={boy._id}>
                                        <td className="col-num">{i + 1}</td>
                                        <td>
                                            <div className="cust-cell">
                                                {boy.profilePicture?.url ? (
                                                    <img src={boy.profilePicture.url} alt="" className="cust-av" style={{ borderRadius: '50%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div className="cust-av">{getInitials(boy.name)}</div>
                                                )}
                                                <div>
                                                    <div className="cust-name">{boy.name || 'Delivery Partner'}</div>
                                                    <div className="cust-sub">{boy._id?.slice(-6).toUpperCase()}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td><strong>{boy.contact || '—'}</strong></td>
                                        <td className="user-email-cell">{boy.email || '—'}</td>
                                        <td>
                                            <span className="muted" style={{ fontSize: 11 }}>
                                                {getAddressStr(boy.address)}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className="badge badge-orange">
                                                {Array.isArray(boy.currentOrders) ? boy.currentOrders.length : 0}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span className="num-cell">
                                                {Array.isArray(boy.completedOrders) ? boy.completedOrders.length : 0}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={`store-badge ${boy.isActive !== false ? 'store-open' : 'store-closed'}`}>
                                                <span className="store-dot" />
                                                {boy.isActive !== false ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeliveryBoyList;
